import { useState, MouseEvent } from "react";
import styles from "./style.module.scss";
import VectorIcon from "../../../assets/icons/VactorIcon";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { getTempExportCount } from "../../../redux/SingleDataset/selectors";
import MultiSelect, { Option } from "../../uikit/FormFields/SelectList";
import VLButton from "../../uikit/Buttons/VLButton";
import AddTagIcon from "../../../assets/icons/AddTagIcon";
import { useExportSelection } from "../../../hooks/useExportSelection";
import VLFloatGroup from "views/uikit/VLFloatGroup";
import { ActionItem } from "views/pages/DataPage/CardsView/DataList";
import NotesIcon from "assets/icons/NotesIcon";
import { FilterOption } from "types";
import TagsDropDown from "views/components/Cart/TagsDropDown";

type FloatingPanelType = "buttons" | "selectList" | "actions";

interface BaseFloatingPanelProps {
  type: FloatingPanelType;
}

interface FloatingPanelButtonsProps extends BaseFloatingPanelProps {
  type: "buttons";
  actionList: ActionItem[];
}

interface FloatingPanelSelectListProps extends BaseFloatingPanelProps {
  type: "selectList";
  options: Option[];
  onOptionSelect: (selectedValues: string[]) => void | Promise<void>;
}

interface FloatingPanelActionsProps extends BaseFloatingPanelProps {
  type: "actions";
  showTagsDropdown: boolean;
  tagDropdownBtnRef: React.RefObject<HTMLDivElement>;
  allTags: FilterOption[];
  datasetID: string;
  assignedTags: FilterOption[];
  toggleShowTags: (e: MouseEvent) => void;
  onTagSubmission: (tagIDs: number[]) => void;
  onTagRemoval: (tagID: number) => void;
  onAssignTagsClick: () => void;
  onSaveSelection: () => void;
}

type FloatingPanelProps =
  | FloatingPanelButtonsProps
  | FloatingPanelSelectListProps
  | FloatingPanelActionsProps;

export default function FloatingPanel({ ...props }: FloatingPanelProps) {
  const [showMultiSelect, setShowMultiSelect] = useState(false);
  const exportCount = useSelector(getTempExportCount);
  const { handleClearAll, handleSelectAll, handleMultiSelectChange } =
    useExportSelection();

  const renderByType: Record<FloatingPanelType, React.ReactNode> = {
    selectList: (
      <>
        <VLButton
          type="primary"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowMultiSelect((prev) => !prev);
          }}
          text={"Assign Label"}
          tooltip="Add the selected images as seeds for a label"
          icon={<AddTagIcon />}
          className={styles.vlButtonWrapper}
        />
        {showMultiSelect && (
          <div className={styles.multiSelectDropdown}>
            <MultiSelect
              mode="multiple"
              options={(props as FloatingPanelSelectListProps).options}
              open={showMultiSelect}
              placement="topLeft"
              placeholder="Search"
              onDropdownVisibleChange={(visible: boolean) =>
                setShowMultiSelect(visible)
              }
              onChange={(selectedValues) =>
                handleMultiSelectChange(
                  selectedValues,
                  (props as FloatingPanelSelectListProps).onOptionSelect,
                )
              }
            />
          </div>
        )}
      </>
    ),
    buttons: (
      <div>
        <VLFloatGroup
          actionList={(props as FloatingPanelButtonsProps).actionList}
          placement="topRight"
          buttonSize="large"
        />
      </div>
    ),
    actions:
      props.type === "actions" ? (
        <div className={styles.actionsWrapper}>
          <div className={styles.tagButtonWrapper}>
            <VLButton
              type="primary"
              onClick={props.onAssignTagsClick}
              text={"Assign Tag"}
              icon={<AddTagIcon />}
              className={classNames(styles.vlButtonWrapper, styles.assignTag)}
            />
            {props.showTagsDropdown && (
              <div className={styles.tagsDropdownWrapper}>
                <TagsDropDown
                  caller="floatingBar"
                  allTags={props.allTags}
                  datasetID={props.datasetID}
                  btnRef={props.tagDropdownBtnRef}
                  assignedTags={props.assignedTags}
                  toggleShowTags={props.toggleShowTags}
                  onTagSubmission={props.onTagSubmission}
                  onTagRemoval={props.onTagRemoval}
                />
              </div>
            )}
          </div>
          <div className={styles.selectionWrapper}>
            <VLButton
              type="default"
              text="Keep Selection"
              tooltip="Save this selection to the cart"
              icon={<NotesIcon />}
              onClick={props.onSaveSelection}
              className={classNames(
                styles.selectionButton,
                styles.vlButtonWrapper,
              )}
            />
          </div>
        </div>
      ) : null,
  };

  const selectionButtons = (
    <>
      <div className={styles.selectionButtonContainer}>
        <div className={classNames(styles.buttonWrapper, styles.child)}>
          <VLButton
            type="text"
            onClick={handleSelectAll}
            text={"Select All"}
            className={styles.vlButtonWrapper}
          />
        </div>
        <div className={classNames(styles.buttonDivider, styles.child)} />
        <div className={classNames(styles.buttonWrapper, styles.child)}>
          <VLButton
            type="text"
            onClick={handleClearAll}
            text={"Clear Selection"}
            className={classNames(
              styles.vlButtonWrapper,
              styles.clearSelection,
            )}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.innerWrapper}>
        <div className={classNames(styles.selectedCount, styles.child)}>
          <div className={styles.vectorIcon}>
            <VectorIcon />
          </div>
          <span className={styles.count}>{exportCount}</span>
        </div>
        <div className={classNames(styles.divider, styles.child)} />
        <div className={classNames(styles.multiSelectWrapper, styles.child)}>
          {renderByType[props.type]}
          {selectionButtons}
        </div>
      </div>
    </div>
  );
}
